import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { type PopoverSide, useClampToViewport } from './useClampToViewport'

/**
 * Open state for a popover, the two ways out of one, and the guarantee that it
 * is on screen.
 *
 * ## One dismissal
 *
 * Extracted so there is a single dismissal in the app rather than one per
 * component. The design system is explicit about the failure this avoids —
 * "how we end up with four menus that dismiss differently" — and the way that
 * happens is never a decision; it is the third component writing its own
 * click-outside listener because copying twenty lines was easier than moving
 * them.
 *
 * ## One clamp
 *
 * A popover must be fully visible and still attached to what opened it — see
 * `useClampToViewport`, which every floating thing in the app shares, tooltips
 * included. Doing it here means a new popover gets it by construction rather
 * than by somebody remembering.
 */
export function usePopover({
  /**
   * Whether a click outside closes it. True for anything that floats *beside* the
   * thing that opened it.
   *
   * Off for a caller whose open surface is a **panel of its own** — a Sheet, drawn
   * into a portal at the end of the document. Nothing in it is a descendant of the
   * trigger, so "outside" describes the whole surface: the first click on a select
   * inside the panel dismissed the panel, and the filters read as a control that
   * closed when you used it. A panel already has its own three ways out.
   */
  dismissOnOutside = true,
  /**
   * Which side of the trigger the panel opens on. See `PopoverSide` — the
   * default is the drop-down every piece of chrome wants, and `backward` is
   * for a panel opened from inside something the reader is moving through.
   */
  side = 'forward',
}: {
  dismissOnOutside?: boolean
  side?: PopoverSide
} = {}): {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  /** Put this on the element that counts as "inside". */
  ref: RefObject<HTMLDivElement | null>
  /** Put this on the floating panel itself, so it can be measured and nudged. */
  panelRef: RefObject<HTMLDivElement | null>
} {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || !dismissOnOutside) return
    const onClickOutside = (e: MouseEvent) => {
      if (!(e.target instanceof Node) || !ref.current) return
      // The panel counts as inside even when it is portalled out of the
      // trigger's subtree — otherwise selecting a word in it dismisses it.
      if (
        !ref.current.contains(e.target) &&
        !panelRef.current?.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    /*
     * Going somewhere closes it, and this is the third way out rather than a
     * special case of the first: a menu ITEM is inside the panel, so choosing
     * one dismisses nothing, and a page that changes under an open menu leaves
     * it floating over a page it does not belong to. It is a dismissal, so it
     * belongs with the other two rather than in whichever component noticed.
     */
    const onNavigate = () => setOpen(false)
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKey)
    window.addEventListener('popstate', onNavigate)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('popstate', onNavigate)
    }
  }, [open, dismissOnOutside])

  useClampToViewport(panelRef, open, side)

  return { open, setOpen, ref, panelRef }
}
